import { supabase } from '../supabase';

// ── Types ──

export interface MfaEnrollment {
  id: string;
  user_id: string;
  factor_type: 'totp' | 'sms' | 'email';
  verified: boolean;
  verified_at: string | null;
  last_used_at: string | null;
  created_at: string;
}

export interface LoginAttempt {
  id: string;
  email: string;
  ip_address: string | null;
  user_agent: string | null;
  success: boolean;
  failure_reason: string | null;
  created_at: string;
}

export interface ActiveSession {
  id: string;
  user_id: string;
  session_token: string;
  ip_address: string | null;
  user_agent: string | null;
  device_info: string | null;
  last_active_at: string;
  created_at: string;
}

// ── MFA Enrollment ──

export async function getMfaEnrollments(userId: string): Promise<MfaEnrollment[]> {
  const { data, error } = await supabase
    .from('mfa_enrollments')
    .select('id, user_id, factor_type, verified, verified_at, last_used_at, created_at')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });
  if (error) throw new Error(error.message);
  return (data ?? []) as MfaEnrollment[];
}

export async function enrollMfaTotp(userId: string): Promise<{ factorId: string; totp: { qr_code: string; secret: string; uri: string } }> {
  // Use Supabase Auth MFA API
  const { data, error } = await supabase.auth.mfa.enroll({
    factorType: 'totp',
    friendlyName: 'Authenticator App',
  });
  if (error) throw new Error(error.message);

  // Track in our enrollments table
  await supabase.from('mfa_enrollments').upsert({
    user_id: userId,
    factor_type: 'totp',
    verified: false,
    updated_at: new Date().toISOString(),
  }, { onConflict: 'user_id,factor_type' });

  return {
    factorId: data.id,
    totp: {
      qr_code: data.totp.qr_code,
      secret: data.totp.secret,
      uri: data.totp.uri,
    },
  };
}

export async function verifyMfaTotp(factorId: string, code: string, userId: string): Promise<void> {
  const { data: challenge, error: challengeError } = await supabase.auth.mfa.challenge({ factorId });
  if (challengeError) throw new Error(challengeError.message);

  const { error: verifyError } = await supabase.auth.mfa.verify({
    factorId,
    challengeId: challenge.id,
    code,
  });
  if (verifyError) throw new Error(verifyError.message);

  // Mark as verified in our table
  await supabase.from('mfa_enrollments')
    .update({ verified: true, verified_at: new Date().toISOString(), updated_at: new Date().toISOString() })
    .eq('user_id', userId)
    .eq('factor_type', 'totp');
}

export async function challengeAndVerifyMfa(factorId: string, code: string): Promise<void> {
  const { data: challenge, error: challengeError } = await supabase.auth.mfa.challenge({ factorId });
  if (challengeError) throw new Error(challengeError.message);

  const { error: verifyError } = await supabase.auth.mfa.verify({
    factorId,
    challengeId: challenge.id,
    code,
  });
  if (verifyError) throw new Error('Invalid verification code. Please try again.');
}

export async function unenrollMfa(factorId: string, userId: string): Promise<void> {
  const { error } = await supabase.auth.mfa.unenroll({ factorId });
  if (error) throw new Error(error.message);

  await supabase.from('mfa_enrollments')
    .delete()
    .eq('user_id', userId)
    .eq('factor_type', 'totp');
}

export async function getMfaFactors(): Promise<{ totp: { id: string; friendly_name: string; status: string }[] }> {
  const { data, error } = await supabase.auth.mfa.listFactors();
  if (error) throw new Error(error.message);
  return {
    totp: (data.totp ?? []).map(f => ({
      id: f.id,
      friendly_name: f.friendly_name ?? 'Authenticator',
      status: f.status,
    })),
  };
}

// ── Login Attempts ──

export async function getLoginAttempts(limit = 50): Promise<LoginAttempt[]> {
  const { data, error } = await supabase
    .from('login_attempts')
    .select('*')
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) throw new Error(error.message);
  return (data ?? []) as LoginAttempt[];
}

export async function getLoginAttemptsByEmail(email: string, limit = 20): Promise<LoginAttempt[]> {
  const { data, error } = await supabase
    .from('login_attempts')
    .select('*')
    .eq('email', email)
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) throw new Error(error.message);
  return (data ?? []) as LoginAttempt[];
}

export async function recordLoginAttempt(email: string, success: boolean, failureReason?: string): Promise<void> {
  await supabase.rpc('record_login_attempt', {
    p_email: email,
    p_success: success,
    p_failure_reason: failureReason ?? null,
  });
}

export async function isAccountLocked(email: string): Promise<boolean> {
  const { data, error } = await supabase.rpc('check_account_lockout', { p_email: email });
  if (error) return false;
  return data as boolean;
}

// ── Active Sessions ──

export async function getActiveSessions(userId: string): Promise<ActiveSession[]> {
  const { data, error } = await supabase
    .from('active_sessions')
    .select('*')
    .eq('user_id', userId)
    .order('last_active_at', { ascending: false });
  if (error) throw new Error(error.message);
  return (data ?? []) as ActiveSession[];
}

export async function revokeSession(sessionId: string): Promise<void> {
  const { error } = await supabase
    .from('active_sessions')
    .delete()
    .eq('id', sessionId);
  if (error) throw new Error(error.message);
}

export async function revokeAllSessions(userId: string, exceptSessionId?: string): Promise<void> {
  let query = supabase.from('active_sessions').delete().eq('user_id', userId);
  if (exceptSessionId) {
    query = query.neq('id', exceptSessionId);
  }
  const { error } = await query;
  if (error) throw new Error(error.message);
}

// ── MFA Requirement Check ──

export interface MfaGate {
  /** The session has not reached aal2 but the user has a factor that could take it there. */
  required: boolean;
  /** The user has at least one verified TOTP factor. */
  enrolled: boolean;
  factorId: string | null;
  /** Server-reported assurance level of the current session. */
  currentLevel: string | null;
}

/**
 * Decide whether this session still owes an MFA challenge.
 *
 * **Every input is server-issued.** `listFactors()` and `getAuthenticatorAssuranceLevel()`
 * read the enrolled factors and the `aal` claim inside the JWT that GoTrue signed. Nothing
 * a user, a script, or an extension can write to the browser reaches this decision.
 *
 * This function previously trusted `localStorage['nizamone_mfa_verified_<userId>']` as proof
 * that "MFA was done on this device before" and returned `required:false` on sight of it —
 * so anyone holding a stolen password could set the key and skip the second factor entirely.
 * The trust decision now lives where it cannot be forged: in the token.
 *
 * Fails CLOSED. A lookup error is not "no MFA needed" — it is "we do not know", and the
 * callers turn that into a denied login. The old code returned `required:false` when
 * `listFactors()` errored, which handed an attacker a second way in (break the call, skip
 * the factor).
 */
export async function checkMfaRequired(): Promise<MfaGate> {
  const { data: factors, error: factorsError } = await supabase.auth.mfa.listFactors();
  if (factorsError) throw new Error(`MFA factor lookup failed: ${factorsError.message}`);

  const verifiedTotp = factors.totp?.find(f => f.status === 'verified') ?? null;

  const { data: aal, error: aalError } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
  if (aalError || !aal) {
    throw new Error(`MFA assurance level lookup failed: ${aalError?.message ?? 'no data returned'}`);
  }

  // GoTrue sets nextLevel='aal2' only when the user actually has a verified factor, so a
  // user without one is never asked for a code and cannot be locked out by this check.
  // `currentLevel !== 'aal2'` (rather than `=== 'aal1'`) keeps it closed for any future
  // level value we do not recognise.
  const required = aal.nextLevel === 'aal2' && aal.currentLevel !== 'aal2';

  return {
    required,
    enrolled: !!verifiedTotp,
    factorId: verifiedTotp?.id ?? null,
    currentLevel: aal.currentLevel ?? null,
  };
}

/**
 * Delete the retired device-trust keys on startup.
 *
 * `nizamone_mfa_verified_<userId>` used to skip the MFA challenge. It is no longer read,
 * so a leftover value is inert — but it is removed anyway: an attacker who planted one
 * expects it to work, an auditor who finds one cannot tell it is dead, and leaving it
 * invites someone to wire it back up. Deleted outright, never migrated or used as a
 * fallback — there is nothing in it worth keeping.
 */
export function purgeLegacyMfaTrustKeys(): number {
  const PREFIX = 'nizamone_mfa_verified_';
  const victims: string[] = [];
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k?.startsWith(PREFIX)) victims.push(k);
    }
    victims.forEach(k => localStorage.removeItem(k));
  } catch { /* private mode / disabled storage */ }
  return victims.length;
}
